import { addDaysYmd, formatInZone, startOfDayUtc } from '@wit-support/ui-domain';
/**
 * Период экранов «Обзор», «Публикации», «Журнал решений» — одна схема адреса
 * и одна зона.
 *
 * **Бизнес-дни.** День периода — сутки по зоне расписания движка
 * (`schedule.config.json` → `timezone`, сейчас Лос-Анджелес). В этой же зоне
 * движок режет дни и недели рядов `/stats/series` (и возвращает её в ответе как
 * `timezone`), и в ней же публикуются слоты. Поэтому «1–3 октября» в фильтре
 * «Публикаций» и точка «1 октября» на графике «Обзора» — одни и те же сутки, у
 * любого админа в любой зоне. Даты отдельных строк (время публикации) экраны
 * по-прежнему показывают в зоне админа.
 *
 * **Адрес.**
 * - `period` ∈ `7d|90d|all` — готовый период (28 дней — по умолчанию, в адрес
 *   не пишется; `all` — «Всё время», только где он есть);
 * - `from`/`to` — свой период, дни `YYYY-MM-DD`, оба конца включительно
 *   (`to` можно опустить — «по сегодня»). Принимается и ISO-момент из ссылок;
 * - старая ссылка «всё время» `from=2000-01-01` без `to` понимается как `all`.
 *
 * Готовый период уходит в admin-api как `period` — «последние N дней» считает
 * сервер, часы браузера не участвуют.
 */
export const CONTENT_BUSINESS_TZ = 'America/Los_Angeles';
export const PERIOD_PRESETS = ['7d', '28d', '90d'];
export const DEFAULT_PERIOD = '28d';
const PRESET_DAYS = { '7d': 7, '28d': 28, '90d': 90 };
/** «Всё время» для admin-api: раньше этой даты в движке ничего нет. */
export const ALL_TIME_FROM = '2000-01-01';
const YMD = /^\d{4}-\d{2}-\d{2}$/;
function validBoundary(v) {
    if (!v)
        return undefined;
    if (YMD.test(v))
        return startOfDayUtc(v, 'UTC') ? v : undefined;
    return Number.isNaN(Date.parse(v)) ? undefined : v;
}
/** Период из адреса. `allowAll: false` — у экрана нет «Всего времени». */
export function readUrlPeriod(s, opts = {}) {
    const allowAll = opts.allowAll ?? true;
    const from = validBoundary(s.from);
    const to = validBoundary(s.to);
    if (from === ALL_TIME_FROM && !to)
        return { choice: allowAll ? 'all' : DEFAULT_PERIOD };
    if (from || to)
        return { choice: 'custom', ...(from ? { from } : {}), ...(to ? { to } : {}) };
    if (s.period === 'all')
        return { choice: allowAll ? 'all' : DEFAULT_PERIOD };
    const preset = PERIOD_PRESETS.find((p) => p === s.period);
    return { choice: preset ?? DEFAULT_PERIOD };
}
/** Правка адреса для периода; значения по умолчанию не пишутся. */
export function periodPatch(p) {
    if (p.choice === 'custom')
        return { period: null, from: p.from ?? null, to: p.to ?? null };
    return { period: p.choice === DEFAULT_PERIOD ? null : p.choice, from: null, to: null };
}
function boundaryIso(value, end, tz) {
    if (!value)
        return undefined;
    if (YMD.test(value))
        return startOfDayUtc(end ? addDaysYmd(value, 1) : value, tz);
    const t = Date.parse(value);
    return Number.isNaN(t) ? undefined : new Date(t).toISOString();
}
/**
 * Период для admin-api: готовый — `period`; «всё время» — `from` с 2000 года;
 * свой — ISO-границы суток по бизнес-зоне, `to` — начало дня ПОСЛЕ последнего.
 * Перепутанные концы меняются местами (admin-api отвечает 400 на `from ≥ to`).
 */
export function resolvePeriod(p, tz = CONTENT_BUSINESS_TZ) {
    if (p.choice === 'all') {
        const from = startOfDayUtc(ALL_TIME_FROM, tz);
        return from ? { from } : {};
    }
    if (p.choice !== 'custom')
        return { period: p.choice };
    let f = boundaryIso(p.from, false, tz);
    let t = boundaryIso(p.to, true, tz);
    if (!f && !t)
        return { period: DEFAULT_PERIOD };
    if (f && t && f >= t) {
        f = boundaryIso(p.to, false, tz);
        t = boundaryIso(p.from, true, tz);
    }
    return { ...(f ? { from: f } : {}), ...(t ? { to: t } : {}) };
}
/** Сегодня `YYYY-MM-DD` по бизнес-зоне. */
export function businessToday(now = new Date()) {
    return formatInZone(now, 'yyyy-MM-dd', CONTENT_BUSINESS_TZ);
}
/** Свой период при первом выборе — последние 28 дней по сегодня (бизнес-зона). */
export function defaultCustomBounds(now = new Date()) {
    const today = businessToday(now);
    return { from: addDaysYmd(today, -PRESET_DAYS[DEFAULT_PERIOD]), to: today };
}
/** Граница из адреса как день бизнес-зоны (для календаря); ISO переводится. */
export function periodBoundaryDay(value) {
    if (!value)
        return undefined;
    if (YMD.test(value))
        return value;
    return formatInZone(value, 'yyyy-MM-dd', CONTENT_BUSINESS_TZ) || undefined;
}
//# sourceMappingURL=period.js.map